import "./StageFields.css"
import loadingImg from "web/images/loading.gif"

import { Suspense, useState } from "react"
import classNames from "classnames"
import equal from "fast-deep-equal"

import { useCurrentTLO } from "web/hooks/context"
import { useFields, useDealAssetsMissingMandatoryFields } from "web/hooks/data"

import {
  fieldIsVisible,
  isInputableField,
  getFormValue,
  getGlobalValue,
} from "web/components/Form/helpers"
import { Button, Field, Form, Accordion } from "web/components"

import { isNotAValue, plainObjectFromValue } from "web/services/helpers"

import PortfolioMissing from "./PortfolioMissing"
import AssetsMissingMandatoryFields from "./AssetsMissingMandatoryFields"

function StageFields({
  isOpen,
  onToggle,
  deal,
  dealFields,
  loadingAssetStageFields,
}) {
  const [fieldBeingUpdated, setFieldBeingUpdated] = useState()

  const [dealAssetsMissingMandatoryFields] =
    useDealAssetsMissingMandatoryFields(deal.id)
  const { missing_stage_fields: missingDealStageFields } = deal

  const formValue = getFormValue(deal.values, dealFields)
  const globalValue = getGlobalValue(deal.values, dealFields)
  const dataForConditions = plainObjectFromValue(globalValue)

  const formFields = missingDealStageFields.map((mf) => ({
    ...dealFields.find((df) => df.id === mf.field.id),
    mandatory: mf.mandatory,
  }))

  const visibleFormFields = formFields
    .filter(
      (field) =>
        fieldIsVisible(field, dataForConditions) && isInputableField(field)
    )
    .sort((a, b) => b.mandatory - a.mandatory)

  const portfolioMissing = formFields.find((f) => f.key === "deal_portfolios")

  const nonFieldItemCount =
    dealAssetsMissingMandatoryFields.length + (portfolioMissing ? 1 : 0)

  const itemCount = visibleFormFields.length + nonFieldItemCount
  const hasItems = itemCount > 0

  const hasAdditionalFields = visibleFormFields.length > 5
  const numberOfAdditionalFields = visibleFormFields.length - 5

  const requiredVisibleFields = missingDealStageFields.filter(
    (f) => f.mandatory && visibleFormFields.find((vf) => vf.id === f.field.id)
  )

  const requiredItemCount = requiredVisibleFields.length + nonFieldItemCount

  async function handleFieldChanged(key, value) {
    const noValueInForm = isNotAValue(formValue[key])
    const noNewValue = isNotAValue(value)

    if (noValueInForm && noNewValue) return

    const valueChanged = noValueInForm || !equal(formValue[key], value)
    if (!valueChanged) return

    setFieldBeingUpdated(formFields.find((f) => f.key === key))

    const update = { [key]: value }
    const valueBlob = (x) => ({ value: x, form_value: x })

    const formattedUpdate = Object.keys(update).reduce(
      (result, key) => ({ ...result, [key]: valueBlob(update[key]) }),
      {}
    )

    const newLocalData = {
      ...deal,
      values: { ...deal.values, ...formattedUpdate },
    }

    deal.update(update, newLocalData)
  }

  function goToDetails() {
    window.Turbo.visit(`${window.location.pathname}/details`)
  }

  return (
    <Accordion
      title="Missing Details"
      isOpen={isOpen}
      onToggle={onToggle}
      notificationCount={requiredItemCount}
    >
      {hasItems && (
        <Form
          value={formValue}
          globalValue={globalValue}
          onFieldChanged={handleFieldChanged}
          fields={formFields}
          render={() => {
            return (
              <div>
                <div className="grid gap-y-3">
                  {visibleFormFields.slice(0, 5).map((field) => {
                    const isFieldBeingUpdated =
                      fieldBeingUpdated && fieldBeingUpdated.key === field.key
                    const fieldClass = classNames("action-panel-field p-2", {
                      "bg-primary-10 rounded": isFieldBeingUpdated,
                      mandatory: field.mandatory,
                    })

                    return (
                      <div key={field.id} className={fieldClass}>
                        <Field fieldKey={field.key} />
                      </div>
                    )
                  })}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-neutral-4">
                    {hasAdditionalFields &&
                      `And ${numberOfAdditionalFields} more...`}
                  </span>
                </div>
                {portfolioMissing && <PortfolioMissing />}
                {loadingAssetStageFields && <LoadingAssetStageFields />}
                {dealAssetsMissingMandatoryFields.length > 0 && (
                  <AssetsMissingMandatoryFields
                    dealAssets={dealAssetsMissingMandatoryFields}
                    deal={deal}
                  />
                )}
                <div className="mt-4 flex items-center justify-end">
                  <Button
                    variant="secondary_default"
                    size="small"
                    onClick={goToDetails}
                  >
                    All Details
                  </Button>
                </div>
              </div>
            )
          }}
        />
      )}
      {!hasItems && (
        <div className="grid place-items-center py-2 text-sm italic text-neutral-4">
          No details are missing.
        </div>
      )}
    </Accordion>
  )
}

function StageFieldsWithAssetFields({ isOpen, onToggle, deal, dealFields }) {
  return (
    <StageFields
      isOpen={isOpen}
      onToggle={onToggle}
      deal={deal}
      dealFields={dealFields}
      loadingAssetStageFields={false}
    />
  )
}

function LoadingAssetStageFields() {
  return (
    <section className="mt-5">
      <header className="my-3 border-0 border-b border-neutral-9 py-1 text-sm uppercase text-neutral-3">
        Assets
      </header>
      <div className="mt-2 flex flex-col">
        <div className="mx-2 mt-2 flex items-center rounded-md border bg-white p-2.5 text-sm shadow-sm">
          <img src={loadingImg} alt="loading" width="20px" className="mr-2" />
          Loading...
        </div>
      </div>
    </section>
  )
}

export default function StageFieldWrapper({ isOpen, onToggle }) {
  const { data: deal } = useCurrentTLO()
  const dealFields = useFields({ subjectType: "deal" })

  return (
    <Suspense
      fallback={
        <StageFields
          isOpen={isOpen}
          onToggle={onToggle}
          deal={deal}
          dealFields={dealFields}
          missingAssetStageFields={[]}
          loadingAssetStageFields={true}
        />
      }
    >
      <StageFieldsWithAssetFields
        isOpen={isOpen}
        onToggle={onToggle}
        deal={deal}
        dealFields={dealFields}
      />
    </Suspense>
  )
}
